import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/lib/utils";

interface PaginationProps {
  /** Total de items (del `PageMeta` del back). */
  total: number;
  /** Items por página actual. */
  limit: number;
  /** Offset actual (en items, no en páginas — es como el back lo expone). */
  offset: number;
  /** Setea el offset (0, limit, 2*limit, …). */
  onOffsetChange: (next: number) => void;
  /** Setea el limit (10, 25, 50, 100). Default page sizes: `[10, 25, 50, 100]`. */
  onLimitChange?: (next: number) => void;
  /** Opciones de limit. */
  pageSizes?: readonly number[];
  className?: string;
}

const DEFAULT_SIZES = [10, 25, 50, 100] as const;

/**
 * Paginación offset-based (coincide con el `PageMeta` del swagger:
 * `total`, `limit`, `offset`).
 *
 * UI:
 *   [ 1–25 de 342 ]           [ 25 / página ] [ ← ] [ → ]
 *
 * - Si `onLimitChange` no se pasa, el selector de page size se oculta.
 * - Si `total <= limit`, nos ocultamos solos (no hay nada que paginar).
 */
export function Pagination({
  total,
  limit,
  offset,
  onOffsetChange,
  onLimitChange,
  pageSizes = DEFAULT_SIZES,
  className,
}: PaginationProps) {
  if (total <= 0) return null;

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  // Si todo cabe en una página y no permitimos cambiar size, no mostramos nada.
  if (!hasPrev && !hasNext && !onLimitChange) return null;

  return (
    <div
      className={cn(
        "flex flex-col items-start justify-between gap-3 border-t pt-4 sm:flex-row sm:items-center",
        className,
      )}
    >
      <p className="font-mono text-[11px] tabular-nums text-muted-foreground">
        <span className="font-medium text-foreground">
          {from}–{to}
        </span>{" "}
        de <span className="font-medium text-foreground">{total.toLocaleString("es-PE")}</span>
      </p>

      <div className="flex items-center gap-3">
        {onLimitChange ? (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <span>Mostrar</span>
            <Select
              value={String(limit)}
              onValueChange={(v) => {
                const next = Number(v);
                onLimitChange(next);
                // Resetear al principio al cambiar size — más predecible.
                onOffsetChange(0);
              }}
            >
              <SelectTrigger className="h-8 w-[76px] text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizes.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={!hasPrev}
            onClick={() => onOffsetChange(Math.max(0, offset - limit))}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={!hasNext}
            onClick={() => onOffsetChange(offset + limit)}
            aria-label="Página siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
